import { AssessmentApi } from '../../api';
import { convertDateTimeOffsetToSystemTimezone } from '../formatters/date-formatters';
import { mapGender, mapInterventionType } from './gender-mappers';

export interface AssessmentMemberInfoMapped {
  firstName: string;
  lastName: string;
  memberId: string | number;
  gender: ReturnType<typeof mapGender>;
  dateOfBirth: string | Date;
  interventionType: ReturnType<typeof mapInterventionType>;
  gaps: unknown[];
  client: string;
  lineOfBusiness: string;
  state?: string;
  hra: boolean;
  awv: boolean;
  globalVariables: unknown[];
  contactNumber: string;
  email: string;
  address: string;
  plan: string;
  primaryCareProvider: string;
  effectiveDate: string;
  primaryCareProviderNpiNumber: string;
  primaryCareProviderPhoneNumber: string;
  primaryCareProviderFaxNumber: string;
  primaryCareProviderAddress: string;
  appointment?: AssessmentApi.MemberAppointmentResponse;
  encounterDate?: string;
  nearestTakebackSites?: AssessmentApi.DeaTakebackSiteResponse[];
}

/**
 * Maps Assessment member-info API payload to a normalized UI-friendly shape. Consumers can specify a stricter output
 * type when needed.
 */
export function mapAssessmentMemberInfoResponse<T = AssessmentMemberInfoMapped>(
  response: AssessmentApi.MemberInfoResponse
): T {
  if (!response || !response.member) {
    throw new Error('Member information is not available for this appointment.');
  }

  const rawName = response.member?.name?.trim() ?? '';
  const [firstName, ...rest] = rawName.split(' ').filter(Boolean);

  const mapped: AssessmentMemberInfoMapped = {
    firstName: firstName ?? '',
    lastName: rest.join(' '),
    memberId: response.member?.memberId ? String(response.member.memberId) : '',
    gender: mapGender(response.member?.gender),
    dateOfBirth: convertDateTimeOffsetToSystemTimezone(response.member?.dateOfBirth, 'MM-DD-YYYY') || '',
    interventionType: mapInterventionType(response.appointment?.interventionTypeId),
    gaps: response.memberGaps?.map((gap) => gap.measureCode) ?? [],
    client: response.member?.brandName ?? '',
    lineOfBusiness: response.member?.lineOfBusiness ?? '',
    state: response.member?.state ?? undefined,
    hra: response.member?.hra ?? false,
    awv: response.appointment?.awv ?? (response.member as unknown as { awv?: boolean })?.awv ?? false,
    globalVariables: [],
    contactNumber: response.member?.primaryPhoneNumber ?? '',
    email: response.member?.email ?? '',
    address: response.member?.mailingAddress ?? '',
    plan: response.member?.planCode ?? '',
    primaryCareProvider: response.primaryCarePhysician?.name ?? '',
    effectiveDate:
      convertDateTimeOffsetToSystemTimezone(response.primaryCarePhysician?.effectiveDate, 'MM-DD-YYYY') || '',
    primaryCareProviderNpiNumber: response.primaryCarePhysician?.npiNumber ?? '',
    primaryCareProviderPhoneNumber: response.primaryCarePhysician?.phoneNumber ?? '',
    primaryCareProviderFaxNumber: '',
    primaryCareProviderAddress: response.primaryCarePhysician?.address ?? '',
    appointment: response.appointment,
    encounterDate: response.appointment?.encounterDate ?? undefined,
    nearestTakebackSites: response.nearestTakebackSites ?? [],
  };

  return mapped as T;
}
